import { useEffect, useState } from 'react';
import Spinner from '../../../../reusable/spinner/spinner.component';
import Client from '../../../../../tools/client';

import {
  FieldSection,
  FieldGroup,
  Label,
  Toggle,
  ButtonRow,
  Button,
  Description
} from '../../configuration-sections.styles';

const client = new Client();

const CompanyConfigurationDisplay = () => {
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    nameActive: true,
    bioActive: true,
    socialsActive: true,
    socials: {
      facebook: false,
      instagram: false,
      linkedin: false,
      discord: false,
      reddit: false,
      twitter: false,
      youtube: false
    }
  });

  useEffect(() => {
    const fetchData = async () => {
      const config = (await client.setup()).configuration?.app?.company || {};

      setForm({
        nameActive: config.name?.active === 'true' || config.name?.active === true,
        bioActive: config.bio?.active === 'true' || config.bio?.active === true,
        socialsActive: config.socials?.active === 'true' || config.socials?.active === true,
        socials: {
          facebook: config.socials?.facebook?.active === 'true' || config.socials?.facebook?.active === true,
          instagram: config.socials?.instagram?.active === 'true' || config.socials?.instagram?.active === true,
          linkedin: config.socials?.linkedin?.active === 'true' || config.socials?.linkedin?.active === true,
          discord: config.socials?.discord?.active === 'true' || config.socials?.discord?.active === true,
          reddit: config.socials?.reddit?.active === 'true' || config.socials?.reddit?.active === true,
          twitter: config.socials?.twitter?.active === 'true' || config.socials?.twitter?.active === true,
          youtube: config.socials?.youtube?.active === 'true' || config.socials?.youtube?.active === true
        }
      });

      setLoading(false);
    };

    fetchData();
  }, []);

  const handleToggle = (key, nested = false) => {
    if (nested) {
      setForm(prev => ({
        ...prev,
        socials: {
          ...prev.socials,
          [key]: !prev.socials[key]
        }
      }));
    } else {
      setForm(prev => ({ ...prev, [key]: !prev[key] }));
    }
  };

  const handleSave = async () => {
    const updates = {
      'app.company.name.active': String(form.nameActive),
      'app.company.bio.active': String(form.bioActive),
      'app.company.socials.active': String(form.socialsActive),
      'app.company.socials.facebook.active': String(form.socials.facebook),
      'app.company.socials.instagram.active': String(form.socials.instagram),
      'app.company.socials.linkedin.active': String(form.socials.linkedin),
      'app.company.socials.discord.active': String(form.socials.discord),
      'app.company.socials.reddit.active': String(form.socials.reddit),
      'app.company.socials.twitter.active': String(form.socials.twitter),
      'app.company.socials.youtube.active': String(form.socials.youtube)
    };

    for (const [key, value] of Object.entries(updates)) {
      await client.updateConfigurationByKey(key, { value });
    }

    setEditing(false);
  };

  if (loading) return <Spinner />;

  return (
    <FieldSection>
      <FieldGroup>
        <Label>Show Company Name</Label>
        <Description>
          Toggles the visibility of the company name across the site (e.g., in headers, footers, or metadata).
        </Description>
        <Toggle>
          <input
            type="checkbox"
            checked={form.nameActive}
            onChange={() => handleToggle('nameActive')}
            disabled={!editing}
          />
          <span />
        </Toggle>
      </FieldGroup>

      <FieldGroup>
        <Label>Show Company Bio</Label>
        <Description>
          Controls whether a brief company description (bio) is displayed on about pages or in SEO metadata.
        </Description>
        <Toggle>
          <input
            type="checkbox"
            checked={form.bioActive}
            onChange={() => handleToggle('bioActive')}
            disabled={!editing}
          />
          <span />
        </Toggle>
      </FieldGroup>

      <FieldGroup>
        <Label>Show Social Media Links</Label>
        <Description>
          Enables or disables the entire block of social media icons and links across the site.
        </Description>
        <Toggle>
          <input
            type="checkbox"
            checked={form.socialsActive}
            onChange={() => handleToggle('socialsActive')}
            disabled={!editing}
          />
          <span />
        </Toggle>
      </FieldGroup>

      {form.socialsActive && Object.entries(form.socials).map(([platform, isActive]) => (
        <FieldGroup key={platform}>
          <Label>{`Show ${platform.charAt(0).toUpperCase() + platform.slice(1)}`}</Label>
          <Description>
            Toggle whether the {platform} link is shown on your site’s social footer or contact pages.
          </Description>
          <Toggle>
            <input
              type="checkbox"
              checked={isActive}
              onChange={() => handleToggle(platform, true)}
              disabled={!editing}
            />
            <span />
          </Toggle>
        </FieldGroup>
      ))}

      <ButtonRow>
        {editing ? (
          <>
            <Button onClick={() => setEditing(false)}>Cancel</Button>
            <Button primary onClick={handleSave}>Save</Button>
          </>
        ) : (
          <Button primary onClick={() => setEditing(true)}>Edit</Button>
        )}
      </ButtonRow>
    </FieldSection>
  );
};

export default CompanyConfigurationDisplay;